import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { insights } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = insights.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <h1 className="text-2xl font-light text-[#0B1D33] mb-4 tracking-tight">
            Article Not Found
          </h1>
          <p className="text-slate-500 mb-8">
            The article you're looking for doesn't exist.
          </p>
          <Link to="/insights">
            <Button className="bg-[#0B1D33] hover:bg-[#162D4A] text-white">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Insights
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Find adjacent articles for navigation
  const currentIndex = insights.findIndex((a) => a.id === article.id);
  const prevArticle = currentIndex > 0 ? insights[currentIndex - 1] : null;
  const nextArticle = currentIndex < insights.length - 1 ? insights[currentIndex + 1] : null;

  return (
    <div>
      {/* Header */}
      <section className="bg-[#0B1D33] pt-32 pb-16 lg:pt-36 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <button
            onClick={() => navigate('/insights')}
            className="inline-flex items-center text-slate-400 hover:text-white text-sm font-medium mb-8 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            All Insights
          </button>

          <div className="flex items-center gap-3 mb-5">
            <Badge
              variant="secondary"
              className="text-[10px] font-semibold uppercase tracking-wider bg-[#3B82A0]/20 text-[#3B82A0] hover:bg-[#3B82A0]/20"
            >
              {article.category}
            </Badge>
            <span className="text-xs text-slate-400">{article.date}</span>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {article.readTime}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-[2.25rem] font-light text-white leading-tight tracking-tight">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Article Body */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            {/* Excerpt / Lead */}
            <p className="text-lg text-slate-600 leading-relaxed mb-10 font-medium border-l-2 border-[#3B82A0] pl-6">
              {article.excerpt}
            </p>
          </Reveal>

          {/* Content Paragraphs */}
          <div className="space-y-7">
            {article.content.map((paragraph, i) => (
              <Reveal key={i} delay={i * 80}>
                <p className="text-[15.5px] text-slate-600 leading-[1.85]">
                  {paragraph}
                </p>
              </Reveal>
            ))}
          </div>

          <Separator className="my-14" />

          {/* Article Navigation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {prevArticle ? (
              <Link
                to={`/insights/${prevArticle.id}`}
                className="group p-6 rounded-lg border border-slate-200/80 hover:border-[#3B82A0]/30 hover:shadow-md transition-shadow transition-colors duration-300"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 block">
                  Previous
                </span>
                <span className="text-sm font-semibold text-[#0B1D33] group-hover:text-[#3B82A0] transition-colors duration-200 leading-snug block">
                  {prevArticle.title}
                </span>
              </Link>
            ) : (
              <div />
            )}
            {nextArticle && (
              <Link
                to={`/insights/${nextArticle.id}`}
                className="group p-6 rounded-lg border border-slate-200/80 hover:border-[#3B82A0]/30 hover:shadow-md transition-shadow transition-colors duration-300 text-right"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 block">
                  Next
                </span>
                <span className="text-sm font-semibold text-[#0B1D33] group-hover:text-[#3B82A0] transition-colors duration-200 leading-snug block">
                  {nextArticle.title}
                </span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F8FAFC] py-16 lg:py-20 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-xl font-light text-[#0B1D33] mb-4 tracking-tight">
              Ready to Take Action?
            </h2>
            <p className="text-slate-500 max-w-lg mx-auto mb-8 leading-relaxed">
              Our advisory team can help you apply these insights to your
              organization's specific challenges and objectives.
            </p>
            <Link to="/contact">
              <Button className="bg-[#0B1D33] hover:bg-[#162D4A] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200">
                Engage Our Experts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
